import React, { useEffect, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { Upload, Sparkles, Trash2, FileText, Edit3 } from "lucide-react";
import api from "../../lib/api";
import ImagePicker from "../../components/ImagePicker";

export default function Curriculum() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // image being swapped
  const ref = useRef();

  const load = async () => { const { data } = await api.get("/admin/curriculum"); setData(data); };
  useEffect(() => { load(); }, []);

  const swapImage = async (imageId, pick) => {
    try {
      if (pick.photo_id) {
        await api.patch(`/admin/curriculum/images/${imageId}`, { photo_id: pick.photo_id });
      } else if (pick.site_key) {
        await api.patch(`/admin/curriculum/images/${imageId}`, { site_key: pick.site_key });
      } else if (pick.image_base64) {
        await api.patch(`/admin/curriculum/images/${imageId}`, {
          image_base64: pick.image_base64,
          mime_type: pick.mime_type,
        });
      } else if (pick.src) {
        await api.patch(`/admin/curriculum/images/${imageId}`, { src: pick.src });
      }
      toast.success("Image updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Swap failed");
    } finally {
      setEditing(null);
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("PDF only"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("PDF over 25 MB — please compress"); return; }
    setBusy(true);
    try {
      const b64 = await toB64(file);
      toast.info("Analysing curriculum — this can take 20-40 seconds…", { duration: 40000 });
      const { data: res } = await api.post("/admin/curriculum/analyze", {
        filename: file.name, pdf_base64: b64.split(",")[1], term: "",
      }, { timeout: 180000 });
      toast.success(`Parsed ${res.weeks_count} weeks + ${res.images_count} images`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed"); } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!confirm("Clear the curriculum? The Courses page will revert to the default 4-module layout.")) return;
    await api.delete("/admin/curriculum"); await load(); toast.success("Cleared");
  };

  const curr = data?.curriculum;
  const imgs = data?.images || [];

  return (
    <div data-testid="admin-curriculum-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">current term curriculum</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Curriculum</h1>
          <p className="text-sm text-[#6E6074] mt-1">Upload your term's curriculum PDF — the AI extracts the 14 weekly projects + images and updates the public Courses page + Home marquee automatically.</p>
        </div>
        <div className="flex gap-2">
          <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid="curriculum-upload-input" />
          <button onClick={() => ref.current?.click()} disabled={busy} className="sac-btn-primary" data-testid="curriculum-upload-btn">
            <Upload size={16} /> {busy ? "Analysing…" : curr ? "Replace curriculum" : "Upload curriculum PDF"}
          </button>
          {curr && <button onClick={clear} className="sac-btn-secondary" data-testid="curriculum-clear-btn"><Trash2 size={14} /> Clear</button>}
        </div>
      </div>

      {!curr && (
        <div className="admin-surface p-10 text-center">
          <FileText size={36} className="mx-auto text-[#A685BD] mb-3" />
          <div className="font-display text-2xl text-[#5D2C8F]">No curriculum loaded</div>
          <p className="text-sm text-[#6E6074] mt-2 max-w-md mx-auto">Upload the Summer 2026 curriculum PDF and we'll extract all 14 weeks, the term theme, and the project imagery in one go.</p>
        </div>
      )}

      {curr && (
        <>
          <div className="admin-surface p-6 mb-5">
            <div className="flex items-center gap-2 text-[#8EC9C7] text-sm"><Sparkles size={14} /> AI-parsed</div>
            <h2 className="font-display text-2xl text-[#5D2C8F] mt-1">{curr.title}</h2>
            <div className="font-hand text-[#A685BD] text-lg">{curr.term}</div>
            <p className="text-[#5A4A6B] mt-3 leading-relaxed">{curr.overview}</p>
            <div className="text-xs text-[#6E6074] mt-3">{imgs.length} images extracted · last updated {new Date(curr.updated_at).toLocaleString('en-GB')}</div>
          </div>

          <h3 className="font-display text-xl text-[#5D2C8F] mb-3">14 weekly projects</h3>
          <p className="text-xs text-[#6E6074] -mt-1 mb-3">Click any week's image to swap it — pick from your Site Images, Class Photos library, or upload a new one. The change shows on the public Courses page immediately.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {curr.weeks.map((w, idx) => (
              <div key={w.number} className="admin-surface p-4 flex gap-3" data-testid={`admin-week-${w.number}`}>
                {imgs[idx] ? (
                  <button
                    type="button"
                    onClick={() => setEditing(imgs[idx])}
                    className="relative shrink-0 group"
                    data-testid={`swap-week-image-${w.number}`}
                  >
                    <img src={imgs[idx].src} alt="" className="w-20 h-20 rounded-lg object-cover" />
                    <div className="absolute inset-0 bg-[#5D2C8F]/0 group-hover:bg-[#5D2C8F]/50 transition-colors rounded-lg flex items-center justify-center">
                      <Edit3 size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ) : null}
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-widest text-[#A685BD]">Week {w.number} · {w.medium}</div>
                  <div className="font-display text-[#5D2C8F] leading-tight">{w.title}</div>
                  <p className="text-xs text-[#5A4A6B] mt-1 line-clamp-3">{w.project}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="font-display text-xl text-[#5D2C8F] mb-3">Extracted images ({imgs.length})</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {imgs.map((i) => (
              <button
                key={i.id}
                type="button"
                onClick={() => setEditing(i)}
                className="relative group"
                data-testid={`swap-image-${i.id}`}
              >
                <img src={i.src} alt={`page ${i.page}`} className="w-full aspect-square object-cover rounded-lg" />
                <div className="absolute inset-0 bg-[#5D2C8F]/0 group-hover:bg-[#5D2C8F]/50 transition-colors rounded-lg flex items-center justify-center">
                  <Edit3 size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {editing && (
        <ImagePicker
          open={!!editing}
          onClose={() => setEditing(null)}
          onPick={(val) => swapImage(editing.id, val)}
          title="Replace curriculum image"
        />
      )}
    </div>
  );
}

function toB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
}
